import { useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import {
  normalizeMpLegalDocs,
  normalizePublishMode,
  PUBLISH_MODE_LABELS,
  type MiniproPublishMode,
  type MpLegalDocs,
} from '@/lib/mpUserSelfPublish';

type Props = {
  miniprogramId: number;
  platformName: string;
  publishMode: MiniproPublishMode;
  legalDocs: MpLegalDocs;
  onChange: (patch: { publish_mode?: MiniproPublishMode; mp_legal_docs?: MpLegalDocs }) => void;
};

type DocTab = 'user_agreement' | 'privacy_policy' | 'publish_rules';

const DOC_TABS: { key: DocTab; label: string }[] = [
  { key: 'user_agreement', label: '用户协议' },
  { key: 'privacy_policy', label: '隐私政策' },
  { key: 'publish_rules', label: '闲置发布规范' },
];

export function MiniprogramPublishModeCard({
  miniprogramId,
  platformName,
  publishMode,
  legalDocs,
  onChange,
}: Props) {
  const mode = normalizePublishMode(publishMode);
  const docs = normalizeMpLegalDocs(legalDocs, platformName);
  const [docTab, setDocTab] = useState<DocTab>('user_agreement');
  const [resetting, setResetting] = useState(false);

  async function restoreDefaults() {
    if (miniprogramId < 1) return;
    setResetting(true);
    try {
      const r = await apiFetch<{ ok: boolean; mp_legal_docs?: MpLegalDocs }>('get_mp_legal_docs_template', {
        method: 'GET',
        query: { miniprogram_id: miniprogramId, platform_name: platformName },
      });
      if (r.ok && r.mp_legal_docs) {
        onChange({ mp_legal_docs: r.mp_legal_docs });
      }
    } finally {
      setResetting(false);
    }
  }

  return (
    <Card title="发布模式">
      <p className="mb-4 text-sm leading-relaxed text-slate-600">
        默认<strong className="font-medium text-slate-800">传统运营</strong>：与现有产品一致，由站长/表单/管理端维护信息，小程序仅展示与联系。
        切换为<strong className="font-medium text-slate-800">用户自助发布</strong>后，C 端用户可微信登录、绑定手机号并发布二手闲置（先审后发）；下方三份协议文档可在小程序内直接展示。
      </p>
      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        {(Object.keys(PUBLISH_MODE_LABELS) as MiniproPublishMode[]).map((key) => (
          <label
            key={key}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
              mode === key
                ? 'border-teal-500 bg-teal-50/60 ring-1 ring-teal-500/30'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              name="publish_mode"
              className="mt-1"
              checked={mode === key}
              onChange={() => onChange({ publish_mode: key })}
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">{PUBLISH_MODE_LABELS[key]}</span>
              <span className="mt-1 block text-xs text-slate-500">
                {key === 'legacy'
                  ? '不改变现有小程序界面与操作流程（推荐已上线站点保持此项）。'
                  : '启用「我的 / 发闲置」与协议确认；须配置小程序 AppID 与 AppSecret。'}
              </span>
            </span>
          </label>
        ))}
      </div>

      {mode === 'user_self' ? (
        <div className="rounded-xl border border-teal-200/80 bg-teal-50/30 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-teal-950">合规文档（小程序端可直接查看）</h3>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" size="sm" disabled={resetting} onClick={() => void restoreDefaults()}>
                {resetting ? '加载中…' : '恢复默认全文'}
              </Button>
            </div>
          </div>
          <p className="mb-3 text-xs text-teal-900/80">
            版本号：<code className="rounded bg-white/80 px-1">{docs.version}</code>。修改正文后请点击本页「保存」同步至小程序。
          </p>
          <div className="mb-3 flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
            {DOC_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setDocTab(t.key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  docTab === t.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <label className={labelClass}>
            <span>{DOC_TABS.find((t) => t.key === docTab)?.label}正文（Markdown）</span>
            <textarea
              className={`${inputClass} min-h-[320px] font-mono text-xs leading-relaxed`}
              value={docs[docTab]}
              onChange={(e) =>
                onChange({
                  mp_legal_docs: { ...docs, [docTab]: e.target.value },
                })
              }
              spellCheck={false}
            />
          </label>
          <label className={`${labelClass} mt-3 max-w-xs`}>
            <span>文档版本号</span>
            <input
              className={inputClass}
              value={docs.version}
              onChange={(e) =>
                onChange({
                  mp_legal_docs: { ...docs, version: e.target.value.trim() },
                })
              }
              placeholder="2026-06-11"
            />
          </label>
        </div>
      ) : null}
    </Card>
  );
}
